import type { JSX } from "react";
import { useRef, useState } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { useControlContext } from "@frontend/contexts";

import { YearPickerContext } from "./YearPicker.context";
import type { YearPickerProps } from "./YearPicker.types";
import { decadeOf, thisYear } from "./yearMath";

export function YearPicker({
  size,
  layout = "default",
  min,
  max,
  disabled,
  arrow,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  children,
  value,
  defaultValue,
  onValueChange,
}: YearPickerProps): JSX.Element {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;
  const pointerOffsetRef = useRef(0);
  const actionsRef = useRef<BasePopover.Root.Actions>(null);

  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<number | null>(
    defaultValue ?? null,
  );

  const selected = isControlled ? value : internalValue;

  const [viewDecade, setViewDecade] = useState(() =>
    decadeOf(selected ?? thisYear()),
  );

  function selectYear(year: number) {
    if (!isControlled) {
      setInternalValue(year);
    }

    setViewDecade(decadeOf(year));
    onValueChange?.(year);
    actionsRef.current?.close();
  }

  return (
    <YearPickerContext.Provider
      value={{
        size: resolvedSize ?? "md",
        layout,
        disabled: disabled ?? false,
        arrow: arrow ?? true,
        viewDecade,
        selected,
        min: min ?? null,
        max: max ?? null,
        pointerOffsetRef,
        selectYear,
        goToDecade: (delta) => {
          setViewDecade((current) => current + delta * 10);
        },
        goToView: (year) => {
          setViewDecade(decadeOf(year));
        },
      }}
    >
      <BasePopover.Root
        data-slot="year-picker-root"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={modal}
        actionsRef={actionsRef}
      >
        {children}
      </BasePopover.Root>
    </YearPickerContext.Provider>
  );
}
